import { useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, Alert, Box } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import FileDragAndDropField from '../FileDragAndDropField';
import { TemplateApiModel } from '../../../../types/models/template.model';
import { TemplateFileApiModel } from '../../../../types/models/template-file.model';
import { useGetTemplate } from '../../hooks/api/use-templates.hook';
import {
  useCreateTemplateFile,
  useDeleteTemplateFile,
  useGetAllTemplateFiles,
} from '../../hooks/api/use-template-files.hook';
import { LoadingIndicator } from '../LoadingIndicator';
import FileDisplay from '../FileDisplay';
import { useConfirm } from '../../contexts/confirm.context';
import { TEMPLATES_UPLOAD_SIZE_LIMIT } from '../../../../types/constants/template';

export const ManageTemplateFiles = () => {
  const navigate = useNavigate();

  const [showConfirm] = useConfirm();
  const { templateId } = useParams<{ templateId: string }>();

  const {
    template,
    loading: loadingTemplate,
    error: gettingError,
  } = useGetTemplate<TemplateApiModel & { files: File[] }>({ templateId: Number(templateId) });

  const {
    templateFiles,
    loading: loadingTemplateFiles,
    error: gettingFilesError,
  } = useGetAllTemplateFiles({ templateId: Number(templateId) });

  const [createTemplateFile, { error: createError }] = useCreateTemplateFile();

  const [deleteTemplateFile, { error: deleteError }] = useDeleteTemplateFile();

  const [isUploading, setIsUploading] = useState(false);

  const handleDrop = async (droppedFiles: (File & { handle?: FileSystemHandle })[]) => {
    setIsUploading(true);
    try {
      await Promise.allSettled(
        droppedFiles.map(async (file) => await createTemplateFile({ templateId: Number(templateId), file })),
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteTemplateFile = async (file: TemplateFileApiModel) => {
    showConfirm({
      onConfirm: async () => {
        await deleteTemplateFile({ fileName: file.name, templateId: Number(templateId) });
      },
      title: 'Delete Template File',
      message: `Are you sure you want to delete the file "${file.name}"?`,
    });
  };

  const handleDownloadTemplateFile = (file: TemplateFileApiModel) => {
    window.location.href = `${import.meta.env.VITE_CLOUDFRONT_URL}templates/${Number(templateId)}/${encodeURIComponent(file.name)}`;
  };

  const renderContent = () => {
    if (gettingError || gettingFilesError) {
      return (
        <Alert severity="error">
          {gettingError?.response?.data.message ?? gettingFilesError?.response?.data.message}
        </Alert>
      );
    }

    return (
      <Box>
        <FileDragAndDropField
          fileSizeLimit={TEMPLATES_UPLOAD_SIZE_LIMIT}
          onDrop={handleDrop}
          multiple
          error={createError?.response?.data.message ?? deleteError?.response?.data.message}
          accept={{
            'application/acad': ['.dwt'],
          }}
          isUploading={isUploading}
        />
        {templateFiles && templateFiles.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <FileDisplay<TemplateFileApiModel>
              files={[...templateFiles].sort((a, b) => a.name.localeCompare(b.name))}
              onRemove={handleDeleteTemplateFile}
              onDownload={handleDownloadTemplateFile}
            />
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Dialog open maxWidth="md" fullWidth>
      {loadingTemplate || (loadingTemplateFiles && !templateFiles) ? (
        <Box sx={{ p: 4 }}>
          <LoadingIndicator />
        </Box>
      ) : (
        <>
          <DialogTitle>Manage {template?.name ?? 'Template'} File(s)</DialogTitle>
          <DialogContent>{renderContent()}</DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => navigate('..')} color="primary">
              Close
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default ManageTemplateFiles;
